import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import type { AdminAuthContextType } from '../context/AdminAuthContext';
import { useToast } from '../hooks/useToast';

interface AdminHeaderProps {
  title?: string;
  onMenuClick?: () => void;
}

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  path: string;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ title = '管理画面', onMenuClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const adminAuth = useAdminAuth() as AdminAuthContextType;
  const { admin, adminLogout } = adminAuth;
  const { showError } = useToast();

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  // システム管理者専用のヘッダー
  const userRole = 'システム管理者';

  // システム管理者用ナビゲーションアイテム
  const navigationItems: NavigationItem[] = [
    {
      id: 'dashboard',
      label: 'ダッシュボード',
      icon: <DashboardIcon />,
      path: '/admin/dashboard'
    },
    {
      id: 'stores',
      label: '店舗管理',
      icon: <StoreIcon />,
      path: '/admin/stores'
    },
    {
      id: 'users',
      label: 'ユーザー管理',
      icon: <PeopleIcon />,
      path: '/admin/users'
    },
    {
      id: 'reports',
      label: 'レポート',
      icon: <AssessmentIcon />,
      path: '/admin/reports'
    }
  ];

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationMenuAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationMenuAnchor(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    try {
      await adminLogout();
    } catch (error) {
      console.error('Logout error:', error);
      const errorMessage = error instanceof Error ? error.message : 'ログアウトに失敗しました';
      setLogoutError(errorMessage);
      showError(errorMessage, {
        duration: 7000,
        guidance: 'もう一度お試しください。問題が続く場合は、管理者にお問い合わせください。'
      });
    }
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={2}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: { xs: 56, sm: 64 }
        }}
      >
        <Toolbar sx={{ height: '100%' }}>
          {/* モバイルメニューボタン */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="メニューを開く"
              edge="start"
              onClick={onMenuClick || handleMobileMenuToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* ロゴとタイトル */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Avatar
              sx={{
                width: { xs: 32, sm: 40 },
                height: { xs: 32, sm: 40 },
                bgcolor: 'primary.contrastText',
                color: 'primary.main',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 'bold',
                mr: 2
              }}
            >
              ニ
            </Avatar>
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h1"
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* デスクトップナビゲーション */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              {navigationItems.map((item) => (
                <Tooltip key={item.id} title={item.label}>
                  <IconButton
                    color="inherit"
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      backgroundColor:
                        location.pathname === item.path ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
                    }}
                  >
                    {item.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          )}

          {/* アクションボタン */}
          <Stack direction="row" spacing={1} alignItems="center">
            {/* 通知ボタン（将来拡張用） */}
            <Tooltip title="通知">
              <IconButton
                color="inherit"
                onClick={handleNotificationMenuOpen}
                aria-label="通知を表示"
              >
                <Badge badgeContent={0} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* ユーザーメニュー */}
            <Tooltip title="アカウントメニュー">
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
                aria-label="アカウントメニューを開く"
              >
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* モバイルメニュー */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          display: { md: 'none' },
          '& .MuiDrawer-paper': { width: 240 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">メニュー</Typography>
        </Box>
        <Divider />
        <List>
          {navigationItems.map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* ユーザーメニュー */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {admin?.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {userRole}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleUserMenuClose} disabled>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>プロフィール</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUserMenuClose} disabled>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>設定</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUserMenuClose} disabled>
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>ヘルプ</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>ログアウト</ListItemText>
        </MenuItem>
      </Menu>

      {/* 通知メニュー（将来拡張用） */}
      <Menu
        anchorEl={notificationMenuAnchor}
        open={Boolean(notificationMenuAnchor)}
        onClose={handleNotificationMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            新しい通知はありません
          </Typography>
        </Box>
      </Menu>

      {/* スペーサー（固定ヘッダーの高さ分） */}
      <Box sx={{ height: { xs: 56, sm: 64 } }} />

      {/* エラー通知 */}
      <Snackbar
        open={!!logoutError}
        autoHideDuration={7000}
        onClose={() => setLogoutError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setLogoutError(null)} severity="error" sx={{ width: '100%' }}>
          {logoutError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminHeader;
